// Test Data Configuration
// This file contains all mock/test data used for development and testing

// Check localStorage for persistent overrides
const getStoredOverride = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(`testData_${key}`);
    return stored !== null ? JSON.parse(stored) : defaultValue;
  } catch (error) {
    console.error('Error reading test data override:', error);
    return defaultValue;
  }
};

// Test mode configuration
export const TEST_CONFIG = {
  // Enable/disable test mode - check localStorage first, then fallback to env/url
  get ENABLED() {
    const override = getStoredOverride('enabled', null);
    if (override !== null) return override;
    return process.env.NODE_ENV === 'development' || window.location.search.includes('dev=true');
  },
  
  // Individual feature toggles
  FEATURES: {
    get MOCK_PROJECTS() {
      return getStoredOverride('mockProjects', true);
    },
    MOCK_CHATS: false, // Can add mock chats later if needed
    MOCK_USERS: false, // Can add mock users later if needed
  }
};

// Mock project data
export const MOCK_PROJECTS = [
  {
    id: '64c8f5e8d1234567890abcd1', // Valid MongoDB ObjectId format
    name: 'Design System Revamp',
    memberCount: 8,
    members: [
      { 
        id: 'user-1', 
        firstName: 'Sarah', 
        lastName: 'Johnson', 
        name: 'Sarah Johnson',
        profileImage: { 
          url: 'https://plus.unsplash.com/premium_photo-1689977968861-9c91dbb16049?w=150&h=150&fit=crop&crop=face' 
        } 
      },
      { 
        id: 'user-2', 
        firstName: 'Mike', 
        lastName: 'Chen', 
        name: 'Mike Chen',
        profileImage: null 
      },
      { 
        id: 'user-3', 
        firstName: 'Emma', 
        lastName: 'Davis', 
        name: 'Emma Davis',
        profileImage: { 
          url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face' 
        } 
      },
      { 
        id: 'user-4', 
        firstName: 'Alex', 
        lastName: 'Rodriguez', 
        name: 'Alex Rodriguez',
        profileImage: null 
      },
      { 
        id: 'user-5', 
        firstName: 'Lisa', 
        lastName: 'Wang', 
        name: 'Lisa Wang',
        profileImage: { 
          url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face' 
        } 
      }
    ]
  },
  {
    id: '64c8f5e8d1234567890abcd2', // Valid MongoDB ObjectId format
    name: 'Marketing Campaign',
    memberCount: 5,
    members: [
      { 
        id: 'user-6', 
        firstName: 'David', 
        lastName: 'Kim', 
        name: 'David Kim',
        profileImage: null 
      },
      { 
        id: 'user-7', 
        firstName: 'Rachel', 
        lastName: 'Green', 
        name: 'Rachel Green',
        profileImage: { 
          url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face' 
        } 
      },
      { 
        id: 'user-8', 
        firstName: 'Tom', 
        lastName: 'Wilson', 
        name: 'Tom Wilson',
        profileImage: null 
      },
      { 
        id: 'user-9', 
        firstName: 'Anna', 
        lastName: 'Brown', 
        name: 'Anna Brown',
        profileImage: null 
      }
    ]
  },
  {
    id: '64c8f5e8d1234567890abcd3', // Valid MongoDB ObjectId format
    name: 'Super Bowl Commercial',
    memberCount: 12,
    members: [
      { 
        id: 'user-10', 
        firstName: 'John', 
        lastName: 'Smith', 
        name: 'John Smith',
        profileImage: { 
          url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face' 
        } 
      },
      { 
        id: 'user-11', 
        firstName: 'Maria', 
        lastName: 'Garcia', 
        name: 'Maria Garcia',
        profileImage: null 
      },
      { 
        id: 'user-12', 
        firstName: 'Chris', 
        lastName: 'Taylor', 
        name: 'Chris Taylor',
        profileImage: { 
          url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face' 
        } 
      }
    ]
  },
  {
    id: '64c8f5e8d1234567890abcd4', // Valid MongoDB ObjectId format
    name: 'Backend Infrastructure',
    memberCount: 2,
    members: [
      { 
        id: 'user-13', 
        firstName: 'Jessica', 
        lastName: 'Lee', 
        name: 'Jessica Lee',
        profileImage: null 
      }
    ]
  },
  {
    id: '64c8f5e8d1234567890abcd5', // Valid MongoDB ObjectId format
    name: 'Sales Training',
    memberCount: 6,
    members: [
      { 
        id: 'user-14', 
        firstName: 'Blake', 
        lastName: 'Opp', 
        name: 'Blake Opp',
        profileImage: null 
      },
      { 
        id: 'user-15', 
        firstName: 'Jordan', 
        lastName: 'Miller', 
        name: 'Jordan Miller',
        profileImage: null 
      },
      { 
        id: 'user-16', 
        firstName: 'Casey', 
        lastName: 'Parker', 
        name: 'Casey Parker',
        profileImage: null 
      }
    ]
  }
];

// Utility functions for test data
export const getTestProjects = () => {
  if (!TEST_CONFIG.ENABLED || !TEST_CONFIG.FEATURES.MOCK_PROJECTS) {
    return [];
  }
  return MOCK_PROJECTS;
};

// Function to merge test data with real data
export const mergeWithTestData = (realData, testDataGetter) => {
  if (!TEST_CONFIG.ENABLED) {
    return realData;
  }
  
  const testData = testDataGetter();
  return [...testData, ...realData];
};

// Development helpers
export const logTestConfig = () => {
  if (TEST_CONFIG.ENABLED) {
    console.log('🧪 Test Mode Active');
    console.log('📊 Test Features:', TEST_CONFIG.FEATURES);
    console.log('📁 Mock Projects:', TEST_CONFIG.FEATURES.MOCK_PROJECTS ? MOCK_PROJECTS.length : 0);
  } else {
    console.log('🚀 Production Mode - Test data disabled');
  }
};

// Quick toggle functions for console testing
window.toggleTestMode = () => {
  const currentState = TEST_CONFIG.ENABLED;
  const newState = !currentState;
  
  // Save to localStorage for persistence
  localStorage.setItem('testData_enabled', JSON.stringify(newState));
  
  console.log('🔄 Test mode:', newState ? 'ENABLED' : 'DISABLED');
  console.log('💾 Setting saved - will persist on refresh!');
  console.log('💡 Refresh the page to see changes');
};

window.toggleMockProjects = () => {
  const currentState = TEST_CONFIG.FEATURES.MOCK_PROJECTS;
  const newState = !currentState;
  
  // Save to localStorage for persistence
  localStorage.setItem('testData_mockProjects', JSON.stringify(newState));
  
  console.log('📁 Mock projects:', newState ? 'ENABLED' : 'DISABLED');
  console.log('💾 Setting saved - will persist on refresh!');
  console.log('💡 Refresh the page to see changes');
};

// Additional helper to clear all overrides
window.clearTestOverrides = () => {
  localStorage.removeItem('testData_enabled');
  localStorage.removeItem('testData_mockProjects');
  console.log('🧹 All test overrides cleared - back to default behavior');
  console.log('💡 Refresh the page to see changes');
};

// Helper to show current test status
window.showTestStatus = () => {
  console.log('🧪 Current Test Configuration:');
  console.log('📊 Test Mode:', TEST_CONFIG.ENABLED ? 'ENABLED' : 'DISABLED');
  console.log('📁 Mock Projects:', TEST_CONFIG.FEATURES.MOCK_PROJECTS ? 'ENABLED' : 'DISABLED');
  console.log('💾 Overrides:', {
    testMode: getStoredOverride('enabled', 'default'),
    mockProjects: getStoredOverride('mockProjects', 'default')
  });
};

export default {
  TEST_CONFIG,
  MOCK_PROJECTS,
  getTestProjects,
  mergeWithTestData,
  logTestConfig
}; 